import {
  TRACE_AGENT_NAMES,
  type TraceAgentName,
  type TraceGroupByField,
  type TraceOrderFieldDto,
  type TracePreviewDto,
} from '@terra/shared-contracts-trace';
import {
  DEFAULT_LIFECYCLE_STATE_FILTER,
  type LifecycleStateFilterDto,
} from '@terra/shared-dto-types';
import {
  canAbortTrace,
  canRerunTrace,
  canResumeTrace,
  excludeArchivedTracePreviews,
  isArchivedTracePreview,
} from '@terra/ui-design-system';
import { omit } from 'radash';
import { splitSignalStatusFilterValues } from '@terra/shared-contracts-signals';
import {
  TRACE_STATUS_FILTER_VALUES,
  isTraceListSortableColumn,
  type TraceAgentFilter,
  type TraceListGrouping,
  type TraceListSort,
  type TraceListSortableColumn,
  type TraceResultFilter,
  type TraceStatusFilter,
} from '../../../store/tab/tracesTabScopedFilterStore';

export const EXECUTIONS_PAGE_SIZE = 50;
export const EXECUTIONS_FETCH_ALL_PAGE_SIZE = 500;
export const EXECUTIONS_LOAD_MORE_THRESHOLD_ROWS = 20;

export const shouldLoadMoreExecutions = ({
  lastDisplayedRowIndex,
  loadedCount,
  hasMore,
  isFetchingMore,
}: {
  lastDisplayedRowIndex: number;
  loadedCount: number;
  hasMore: boolean;
  isFetchingMore: boolean;
}) =>
  hasMore &&
  !isFetchingMore &&
  loadedCount > 0 &&
  lastDisplayedRowIndex >= loadedCount - EXECUTIONS_LOAD_MORE_THRESHOLD_ROWS;

export const isExecutionsSortableColumn = (
  colId: string,
): colId is TraceListSortableColumn => isTraceListSortableColumn(colId);

const EXECUTIONS_DEFAULT_ORDER_WITHIN_GROUP: TraceOrderFieldDto = {
  field: 'createdAt',
  order: 'desc',
};

export const toTraceOrderBy = ({
  sort,
  grouping,
}: {
  sort: TraceListSort;
  grouping: TraceListGrouping;
}): TraceOrderFieldDto[] | undefined => {
  const columnOrder: TraceOrderFieldDto[] =
    sort && sort.colId !== grouping?.field
      ? [{ field: sort.colId, order: sort.sort }]
      : [];

  if (!grouping) {
    return columnOrder.length > 0 ? columnOrder : undefined;
  }

  return [
    { field: grouping.field, order: grouping.order },
    ...(columnOrder.length > 0
      ? columnOrder
      : [EXECUTIONS_DEFAULT_ORDER_WITHIN_GROUP]),
  ];
};

export const resolveExecutionsAgentNames = (
  agentFilter: readonly TraceAgentFilter[],
): TraceAgentName[] =>
  agentFilter.length > 0 ? [...agentFilter] : [...TRACE_AGENT_NAMES];

export const buildExecutionsListQuery = ({
  applicationId,
  searchTerm,
  statusFilter,
  resultFilter,
  agentFilter,
  scopedEndpointIds,
  scopedUrlPatterns,
  createdSince,
  lifecycleState,
  sort,
  grouping = null,
}: {
  applicationId: string;
  searchTerm: string;
  statusFilter: readonly TraceStatusFilter[];
  resultFilter: readonly TraceResultFilter[];
  agentFilter: readonly TraceAgentFilter[];
  scopedEndpointIds: string[] | undefined;
  scopedUrlPatterns: string[] | undefined;
  createdSince: Date | null;
  lifecycleState: LifecycleStateFilterDto | undefined;
  sort: TraceListSort;
  grouping?: TraceListGrouping;
}) => {
  const resultFilters = splitSignalStatusFilterValues(resultFilter);

  return {
    applicationId,
    agentName: resolveExecutionsAgentNames(agentFilter),
    name: searchTerm || undefined,
    status: statusFilter.length > 0 ? [...statusFilter] : undefined,
    signalStatus: resultFilters.status,
    blockedReason: resultFilters.blockedReason,
    confirmedVulnerabilityFindingState:
      resultFilters.confirmedVulnerabilityFindingState,
    endpointIds: scopedEndpointIds,
    urlPatterns: scopedUrlPatterns,
    orderBy: toTraceOrderBy({ sort, grouping }),
    startDate: createdSince ?? undefined,
    lifecycleState,
  };
};

export type ExecutionsStatusCountsQuery = Omit<
  ReturnType<typeof buildExecutionsListQuery>,
  'status' | 'orderBy'
>;

export const buildExecutionsStatusCountsQuery = (
  params: Omit<
    Parameters<typeof buildExecutionsListQuery>[0],
    'statusFilter' | 'sort' | 'grouping'
  >,
): ExecutionsStatusCountsQuery =>
  omit(buildExecutionsListQuery({ ...params, statusFilter: [], sort: null }), [
    'status',
    'orderBy',
  ]);

export type ExecutionsGroupCountsQuery = Omit<
  ReturnType<typeof buildExecutionsListQuery>,
  'orderBy'
> & { groupBy: TraceGroupByField };

export const buildExecutionsGroupCountsQuery = ({
  groupBy,
  ...params
}: Omit<Parameters<typeof buildExecutionsListQuery>[0], 'sort' | 'grouping'> & {
  groupBy: TraceGroupByField;
}): ExecutionsGroupCountsQuery => ({
  ...omit(buildExecutionsListQuery({ ...params, sort: null }), ['orderBy']),
  groupBy,
});

export const hasActiveExecutionListFilters = ({
  statusFilter,
  resultFilter,
  agentFilter = [],
}: {
  statusFilter: readonly TraceStatusFilter[];
  resultFilter: readonly TraceResultFilter[];
  agentFilter?: readonly TraceAgentFilter[];
}) =>
  statusFilter.length > 0 || resultFilter.length > 0 || agentFilter.length > 0;

export const hasAnyExecutionFilters = ({
  searchTerm,
  statusFilter,
  resultFilter,
  agentFilter = [],
  createdSince,
  selectableLifecycleState,
}: {
  searchTerm: string;
  statusFilter: readonly TraceStatusFilter[];
  resultFilter: readonly TraceResultFilter[];
  agentFilter?: readonly TraceAgentFilter[];
  createdSince: Date | null;
  selectableLifecycleState: LifecycleStateFilterDto | null;
}) =>
  searchTerm.trim() !== '' ||
  createdSince !== null ||
  hasActiveExecutionListFilters({ statusFilter, resultFilter, agentFilter }) ||
  (selectableLifecycleState !== null &&
    selectableLifecycleState !== DEFAULT_LIFECYCLE_STATE_FILTER);

export const EXECUTIONS_STATUS_CHIP_ALL = 'ALL';

export type ExecutionsStatusChip =
  | typeof EXECUTIONS_STATUS_CHIP_ALL
  | TraceStatusFilter;

export const EXECUTIONS_STATUS_CHIPS: readonly ExecutionsStatusChip[] = [
  EXECUTIONS_STATUS_CHIP_ALL,
  ...TRACE_STATUS_FILTER_VALUES,
];

export type ExecutionsStatusCounts = {
  total: number;
  byStatus: Partial<Record<TraceStatusFilter, number>> | null;
};

export const resolveExecutionsStatusChip = (
  statusFilter: readonly TraceStatusFilter[],
): ExecutionsStatusChip | null => {
  const [onlyStatus] = statusFilter;

  if (statusFilter.length === 0) {
    return EXECUTIONS_STATUS_CHIP_ALL;
  }

  return statusFilter.length === 1 && onlyStatus ? onlyStatus : null;
};

export const toExecutionsStatusFilter = (
  chip: ExecutionsStatusChip,
): TraceStatusFilter[] => (chip === EXECUTIONS_STATUS_CHIP_ALL ? [] : [chip]);

export const resolveExecutionsStatusChipCount = ({
  chip,
  statusCounts,
}: {
  chip: ExecutionsStatusChip;
  statusCounts: ExecutionsStatusCounts | null;
}): number | undefined => {
  if (!statusCounts) {
    return undefined;
  }

  return chip === EXECUTIONS_STATUS_CHIP_ALL
    ? statusCounts.total
    : (statusCounts.byStatus?.[chip] ?? 0);
};

export const resolveExecutionObjective = (trace: {
  signal?: { description?: string | null } | null;
  executionObjective?: string;
}): string | null =>
  trace.signal?.description?.trim() || trace.executionObjective?.trim() || null;

export const getExecutionRowId = (trace: Pick<TracePreviewDto, 'threadId'>) =>
  trace.threadId;

export const resolveNextExecutionsPageSkip = ({
  pages,
}: {
  pages: ReadonlyArray<{ traces: readonly unknown[]; totalCount: number }>;
}): number | undefined => {
  const lastPage = pages.at(-1);
  const loadedCount = pages.reduce((sum, page) => sum + page.traces.length, 0);

  return lastPage &&
    lastPage.traces.length > 0 &&
    loadedCount < lastPage.totalCount
    ? loadedCount
    : undefined;
};

export const EXECUTIONS_SELECT_ALL_STATE = {
  NONE: 'NONE',
  SOME: 'SOME',
  ALL: 'ALL',
} as const;

export type ExecutionsSelectAllState =
  (typeof EXECUTIONS_SELECT_ALL_STATE)[keyof typeof EXECUTIONS_SELECT_ALL_STATE];

export const resolveExecutionsSelectAllState = ({
  areAllSelected,
  selectedCount,
}: {
  areAllSelected: boolean;
  selectedCount: number;
}): ExecutionsSelectAllState => {
  if (areAllSelected) {
    return EXECUTIONS_SELECT_ALL_STATE.ALL;
  }

  return selectedCount > 0
    ? EXECUTIONS_SELECT_ALL_STATE.SOME
    : EXECUTIONS_SELECT_ALL_STATE.NONE;
};

export const resolveSelectableExecutionTotal = ({
  displayedTotalCount,
  lifecycleState,
  mutableTotalCount,
}: {
  displayedTotalCount: number;
  lifecycleState: LifecycleStateFilterDto | undefined;
  mutableTotalCount: number | undefined;
}): number => {
  if (lifecycleState === 'archived') {
    return 0;
  }

  if (lifecycleState === 'all') {
    return mutableTotalCount ?? displayedTotalCount;
  }

  return displayedTotalCount;
};

export const countSelectedExecutions = ({
  isAllFilteredSelected,
  selectableTotalCount,
  excludedCount,
  selectedCount,
}: {
  isAllFilteredSelected: boolean;
  selectableTotalCount: number;
  excludedCount: number;
  selectedCount: number;
}): number =>
  isAllFilteredSelected
    ? Math.max(selectableTotalCount - excludedCount, 0)
    : selectedCount;

export const isResumableExecution = (trace: TracePreviewDto) =>
  canResumeTrace(trace) && !isArchivedTracePreview(trace);

export const isRerunableExecution = (trace: TracePreviewDto) =>
  canRerunTrace(trace) && !isArchivedTracePreview(trace);

export const isAbortableExecution = (trace: TracePreviewDto) =>
  canAbortTrace(trace) && !isArchivedTracePreview(trace);

export const selectMutableExecutions = ({
  traces,
  excludedThreadIds,
  selectedThreadIds,
  isAllFilteredSelected,
}: {
  traces: readonly TracePreviewDto[];
  excludedThreadIds: ReadonlySet<string>;
  selectedThreadIds: ReadonlySet<string>;
  isAllFilteredSelected: boolean;
}): TracePreviewDto[] =>
  excludeArchivedTracePreviews(
    traces.filter((trace) =>
      isAllFilteredSelected
        ? !excludedThreadIds.has(trace.threadId)
        : selectedThreadIds.has(trace.threadId),
    ),
  );
